/**
 * "What's new" release notes: shared types and pure helpers, safe for
 * both server and client code (no fetch, no storage here). The owner
 * writes every release by hand in the admin app; the site only shows
 * them: the changelog (/whats-new), one page per release, the header
 * "What's new" popover, the one-time corner card, the small "New"
 * markers on menu items and the roadmap step on the hub.
 */
import type { Locale } from "@/lib/i18n-config";

/** The four sections of a release, in display order. */
export const RELEASE_KINDS = ["feature", "major", "minor", "fix"] as const;
export type ReleaseKind = (typeof RELEASE_KINDS)[number];
export type ReleaseCounts = Record<ReleaseKind, number>;

export interface ReleaseHighlight {
  slug: string;
  title: string;
  summary: string;
  image_url: string | null;
  href: string | null;
}

/** One release in the list shape (GET /api/releases, /latest). */
export interface ReleaseCard {
  id: number | string;
  slug: string;
  version: string;
  title: string;
  summary: string;
  published_at: string;
  counts: ReleaseCounts;
  highlights: ReleaseHighlight[];
}

export interface ReleaseList {
  items: ReleaseCard[];
  total: number;
  page: number;
  page_size: number;
}

export type ReleaseBlock = { type: "p"; text: string } | { type: "ul"; items: string[] };

export interface ReleaseItem {
  id: number | string;
  slug: string;
  kind: ReleaseKind;
  title: string;
  summary: string;
  body: string;
  blocks: ReleaseBlock[];
  image_url: string | null;
  href: string | null;
  links: { label: string; href: string }[];
}

export interface ReleaseNeighbour {
  slug: string;
  title: string;
  version: string;
}

export interface ReleaseDetail {
  id: number | string;
  slug: string;
  version: string;
  title: string;
  summary: string;
  published_at: string;
  updated_at: string | null;
  sections: Record<ReleaseKind, ReleaseItem[]>;
  /** Older release. */
  prev: ReleaseNeighbour | null;
  /** Newer release. */
  next: ReleaseNeighbour | null;
}

const ZERO_COUNTS: ReleaseCounts = { feature: 0, major: 0, minor: 0, fix: 0 };

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}
function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

/** Defensive parse of a list-shaped release (bad rows become null). */
export function toReleaseCard(raw: unknown): ReleaseCard | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (typeof r.slug !== "string" || !r.slug || typeof r.title !== "string") return null;
  const c = (r.counts ?? {}) as Record<string, unknown>;
  const counts = { ...ZERO_COUNTS };
  for (const k of RELEASE_KINDS) {
    const n = Number(c[k]);
    counts[k] = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }
  const highlights = Array.isArray(r.highlights)
    ? (r.highlights as Record<string, unknown>[])
        .filter((h) => h && typeof h.title === "string" && typeof h.slug === "string")
        .slice(0, 3)
        .map((h) => ({
          slug: str(h.slug),
          title: str(h.title),
          summary: str(h.summary),
          image_url: strOrNull(h.image_url),
          href: strOrNull(h.href),
        }))
    : [];
  return {
    id: (typeof r.id === "number" || typeof r.id === "string") ? r.id : r.slug,
    slug: r.slug,
    version: str(r.version),
    title: r.title,
    summary: str(r.summary),
    published_at: str(r.published_at),
    counts,
    highlights,
  };
}

function toItem(raw: unknown, kind: ReleaseKind): ReleaseItem | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (typeof r.slug !== "string" || !r.slug || typeof r.title !== "string") return null;
  const blocks: ReleaseBlock[] = Array.isArray(r.blocks)
    ? (r.blocks as Record<string, unknown>[]).flatMap((b): ReleaseBlock[] => {
        if (b && b.type === "p" && typeof b.text === "string" && b.text.trim()) return [{ type: "p", text: b.text }];
        if (b && b.type === "ul" && Array.isArray(b.items)) {
          const items = (b.items as unknown[]).filter((x): x is string => typeof x === "string" && !!x.trim());
          return items.length ? [{ type: "ul", items }] : [];
        }
        return [];
      })
    : [];
  const links = Array.isArray(r.links)
    ? (r.links as Record<string, unknown>[])
        .filter((l) => l && typeof l.label === "string" && l.label.trim() && typeof l.href === "string")
        .map((l) => ({ label: str(l.label), href: str(l.href) }))
    : [];
  return {
    id: (typeof r.id === "number" || typeof r.id === "string") ? r.id : r.slug,
    slug: r.slug,
    kind,
    title: r.title,
    summary: str(r.summary),
    body: str(r.body),
    blocks,
    image_url: strOrNull(r.image_url),
    href: strOrNull(r.href),
    links,
  };
}

function toNeighbour(raw: unknown): ReleaseNeighbour | null {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r.slug !== "string" || !r.slug) return null;
  return { slug: r.slug, title: str(r.title), version: str(r.version) };
}

/** Defensive parse of GET /api/releases/{slug}. */
export function toReleaseDetail(raw: unknown): ReleaseDetail | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (typeof r.slug !== "string" || !r.slug || typeof r.title !== "string") return null;
  const secs = (r.sections ?? {}) as Record<string, unknown>;
  const sections = { feature: [], major: [], minor: [], fix: [] } as Record<ReleaseKind, ReleaseItem[]>;
  for (const k of RELEASE_KINDS) {
    const list = secs[k];
    if (Array.isArray(list)) {
      sections[k] = list.map((x) => toItem(x, k)).filter((x): x is ReleaseItem => !!x);
    }
  }
  return {
    id: (typeof r.id === "number" || typeof r.id === "string") ? r.id : r.slug,
    slug: r.slug,
    version: str(r.version),
    title: r.title,
    summary: str(r.summary),
    published_at: str(r.published_at),
    updated_at: strOrNull(r.updated_at),
    sections,
    prev: toNeighbour(r.prev),
    next: toNeighbour(r.next),
  };
}

export function countsOf(d: ReleaseDetail): ReleaseCounts {
  return {
    feature: d.sections.feature.length,
    major: d.sections.major.length,
    minor: d.sections.minor.length,
    fix: d.sections.fix.length,
  };
}

/** Plural form for the count phrases: ru has three, az and en use
 *  "one" / "many" ("few" falls back to "many" in their maps). */
export function pluralForm(n: number, locale: Locale): "one" | "few" | "many" {
  if (locale === "ru") {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return "one";
    if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "few";
    return "many";
  }
  return n === 1 ? "one" : "many";
}

/** Translation key of one count phrase ("{n} major changes"). */
export function countKey(kind: ReleaseKind, n: number, locale: Locale) {
  return `wn.count.${kind}.${pluralForm(n, locale)}` as const;
}

/** Version label without a doubled "v": "2.4" and "v2.4" both give "v2.4". */
export function versionLabel(v: string): string {
  const s = v.trim();
  if (!s) return "";
  return /^v/i.test(s) ? `v${s.slice(1)}` : `v${s}`;
}

/** Days since an ISO date (NaN-safe: unparseable means "long ago"). */
export function ageDays(iso: string, now = Date.now()): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Infinity : (now - t) / 86_400_000;
}

/** A "new" mark on a menu item (the dot, the pill, the release dot) lasts
 *  a week from the launch (quiet UI, owner 2026-10-29): after that the
 *  feature is part of the site, not news. */
export const NEW_MARK_DAYS = 7;

/** The mark is still fresh: launched (``since``, the release's publish
 *  date) less than NEW_MARK_DAYS ago. No launch date known, no mark. */
export function isFreshMark(since: string | null | undefined, now = Date.now()): boolean {
  if (!since) return false;
  return ageDays(since, now) < NEW_MARK_DAYS;
}

export type AnnouncementIntent = "buy" | "rent" | "invest" | "sell" | "rent_out" | "agent" | "commercial" | "garage" | "none";

export interface AnnouncementBadge {
  id: number | string;
  slug: string;
  badge_href: string;
  until: string | null;
  /** The launch: the publish date of the item's release. Older API
   *  answers lack it, and then the mark stays hidden (isFreshMark). */
  since?: string | null;
}

export interface AnnouncementRoadmapItem {
  id: number | string;
  slug: string;
  title: string;
  summary: string;
  href: string | null;
  image_url: string | null;
  intents: AnnouncementIntent[];
  until: string | null;
}

export interface ActiveAnnouncements {
  badges: AnnouncementBadge[];
  roadmap: AnnouncementRoadmapItem[];
}

/** Path without origin, query, hash, locale prefix or trailing slash. */
export function plainPath(raw: string): string {
  let p = raw.trim();
  p = p.replace(/^https?:\/\/[^/]+/i, "");
  p = p.split(/[?#]/)[0] || "/";
  if (!p.startsWith("/")) p = `/${p}`;
  p = p.replace(/^\/(az|ru|en)(?=\/|$)/, "") || "/";
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p || "/";
}

/** True when `path` is the badge path itself or sits under it at a
 *  segment boundary. The site root only ever matches itself. */
export function pathUnder(path: string, badgePath: string): boolean {
  const a = plainPath(path);
  const b = plainPath(badgePath);
  if (a === b) return true;
  if (b === "/") return false;
  return a.startsWith(`${b}/`);
}

/** An expiry in the past hides the item even if a cached API answer
 *  still carries it. Unparseable dates count as no expiry. */
export function isLive(until: string | null | undefined, now = Date.now()): boolean {
  if (!until) return true;
  const t = Date.parse(until);
  if (Number.isNaN(t)) return true;
  // A bare date ("2026-10-20") is the last day the item shows, so it
  // stays live through that whole day.
  const end = /^\d{4}-\d{2}-\d{2}$/.test(until.trim()) ? t + 86_400_000 : t;
  return end > now;
}

/**
 * The feature link as a locale-less in-site path for LocalizedLink
 * (which adds the locale). Anything that is not an in-site path
 * (protocol-relative, javascript:, external) is dropped, so a typo in
 * the admin app can never become an off-site or script link.
 */
export function inSiteHref(href: string | null | undefined): string | null {
  if (!href) return null;
  const h = href.trim();
  if (!h.startsWith("/") || h.startsWith("//")) return null;
  const m = /^([^?#]*)(.*)$/.exec(h);
  const path = plainPath(m?.[1] ?? h);
  return `${path}${m?.[2] ?? ""}`;
}

/** The feature link: an in-site path (locale-less) or an https URL
 *  the owner entered; anything else is dropped. */
export function featureHref(href: string | null | undefined): string | null {
  const inSite = inSiteHref(href);
  if (inSite) return inSite;
  const h = (href ?? "").trim();
  return /^https:\/\/[^\s]+$/i.test(h) ? h : null;
}

/** Relative media path to an absolute URL (social cards, JSON-LD). */
/** Whether a media URL still answers (HEAD 2xx), so a screenshot that
 *  went missing never becomes a 404 social card or schema image. Next
 *  caches the answer for an hour; any failure counts as missing. */
export async function mediaReachable(url: string): Promise<boolean> {
  try {
    const r = await fetch(url, { method: "HEAD", next: { revalidate: 3600 } });
    return r.ok;
  } catch {
    return false;
  }
}

export function absoluteMedia(url: string | null | undefined, site: string): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${site.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
}

// Hand-mapped like lib/blog-format.ts (Node ICU is unreliable for az).
// Dates use the genitive month ("7 oktyabr", "7 октября").
const MONTHS_NOM: Record<Locale, readonly string[]> = {
  az: ["Yanvar", "Fevral", "Mart", "Aprel", "May", "İyun",
       "İyul", "Avqust", "Sentyabr", "Oktyabr", "Noyabr", "Dekabr"],
  ru: ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
       "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"],
  en: ["January", "February", "March", "April", "May", "June",
       "July", "August", "September", "October", "November", "December"],
};
const MONTHS_GEN: Record<Locale, readonly string[]> = {
  az: ["yanvar", "fevral", "mart", "aprel", "may", "iyun",
       "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"],
  ru: ["января", "февраля", "марта", "апреля", "мая", "июня",
       "июля", "августа", "сентября", "октября", "ноября", "декабря"],
  en: MONTHS_NOM.en,
};

function ymd(iso: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return null;
  return [Number(m[1]), mo, Number(m[3])];
}

/** "7 oktyabr 2026" / "7 октября 2026" / "7 October 2026". */
export function formatAnnDate(iso: string, locale: Locale): string {
  const d = ymd(iso);
  if (!d) return iso;
  return `${d[2]} ${MONTHS_GEN[locale][d[1] - 1]} ${d[0]}`;
}

export type BodyBlock = { kind: "p"; text: string } | { kind: "ul"; items: string[] };

/**
 * The plain-text body rule the admin editor documents: a blank line
 * starts a new paragraph, lines starting "- " form a bullet list.
 * Other lines inside a paragraph are joined with a space.
 */
export function parseBody(body: string | null | undefined): BodyBlock[] {
  if (!body) return [];
  const out: BodyBlock[] = [];
  const chunks = body.replace(/\r\n?/g, "\n").split(/\n\s*\n/);
  for (const chunk of chunks) {
    let para: string[] = [];
    let list: string[] = [];
    const flushPara = () => {
      if (para.length) out.push({ kind: "p", text: para.join(" ") });
      para = [];
    };
    const flushList = () => {
      if (list.length) out.push({ kind: "ul", items: list });
      list = [];
    };
    for (const raw of chunk.split("\n")) {
      const line = raw.trim();
      if (!line) continue;
      if (line.startsWith("- ")) {
        flushPara();
        const item = line.slice(2).trim();
        if (item) list.push(item);
      } else {
        flushList();
        para.push(line);
      }
    }
    flushPara();
    flushList();
  }
  return out;
}
